import React, { useEffect, useState } from "react";
import io from "socket.io-client";
import NegotiationChatContainer from "./components/negotiation-chat";
import { api_url } from "../../constant";
import { useParams } from "react-router-dom";
import axios from "axios";
import Client from "../../utils/client";
import { getUnits } from "../../api";
import { useQuery } from "@tanstack/react-query";
import InvoiceModal from "./components/InvoiceModal";
import { toast } from "react-toastify";

function MahsoolNegotiation() {
    const { negotiationId } = useParams(); // Extract negotiationId from the URL
    const [messages, setMessages] = useState([]);
    const [negotiation, setNegotiation] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const socket = React.useRef(null);
    const currentUserId = localStorage.getItem("user_id");
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState({
        quantity: "",
        amount: "",
        dueDate: "",
        notes: "",
        deliveryPlace: "", // New field
        unitId: "", // New field
        deliveryResponsibility: "seller", // New field (default to seller)
        paymentTerms: "", // New field
    });
    const [unreadCount, setUnreadCount] = useState(0);

    const { data: units } = useQuery({
        queryKey: ["units"],
        queryFn: () => getUnits(),
        cacheTime: 99999,
        staleTime: 99999,
    });
    // const token = localStorage.getItem("token")?.replace(/"/g, ''); // Remove accidental quotes
    const [token, setToken] = useState(null);

    useEffect(() => {
        const storedToken = localStorage.getItem("token")?.replace(/"/g, '');
        if (storedToken) {
            setToken(storedToken);
        }
    }, []);

    useEffect(() => {
        if (!negotiationId) {
            console.error("Negotiation ID is missing");
            return;
        }

        const fetchNegotiation = async () => {
            try {
                const response = await axios.get(`${api_url}/negotiations/${negotiationId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setNegotiation(response.data);
            } catch (error) {
                console.error("Failed to fetch Negotiation:", error.message);
            }
        };

        const fetchMessages = async () => {
            try {
                const response = await axios.get(`${api_url}/negotiations/${negotiationId}/messages`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setMessages(response.data);
            } catch (error) {
                console.error("Failed to fetch messages:", error.message);
            }
        };

        const markMessagesAsRead = async () => {
            try {
                await Client.post(`${api_url}/messages/mark-seen/${negotiationId}`, {}, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUnreadCount(0);
            } catch (error) {
                console.error("Failed to mark messages as read:", error.message);
            }
        };

        const IntializeSocketConnection = async () => {
            try {
                const freshToken = localStorage.getItem("token")?.replace(/"/g, '');
                if (!freshToken) {
                    console.error("Socket setup failed: token is missing");
                    return;
                }
                socket.current = io(`${api_url}/messages?negotiationId=${negotiationId}`, {
                    extraHeaders: {
                        Authorization: `Bearer ${freshToken}`,
                    },
                });

                socket.current.emit("joinRoom", `room-${negotiationId}`);

                socket.current.on("newMessage", (message) => {
                    setMessages((prev) => [...prev, message]);
                });

                socket.current.on("error", (errorMessage) => {
                    toast.error(errorMessage, {
                        position: "top-center",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                });
            } catch (error) {
                console.error("Failed to mark messages as read:", error.message);
            }
        };

        fetchNegotiation();
        fetchMessages();
        markMessagesAsRead();
        IntializeSocketConnection();

        return () => {
            if (socket.current) {
                socket.current.disconnect();
            }
        };
    }, [negotiationId, token]);

    const sendMessage = () => {
        if (newMessage.trim()) {
            setSending(true);

            // Emit the message with room and content
            socket.current.emit(
                "sendMessage",
                JSON.stringify({
                    negotiationId: parseInt(negotiationId, 10),
                    content: newMessage,
                })
            );

            setNewMessage("");
            setSending(false);
        }
    };

    const handleInvoiceChange = (e) => {
        const { name, value } = e.target;
        setInvoiceDetails((prev) => ({
            ...prev,
            [name]: name === "quantity" || name === "amount" ? Number(value) : value,
        }));
    };

    const handleIssueInvoice = () => {
        setShowInvoiceModal(true);
    };

    const submitInvoice = async () => {
        try {
            const token = localStorage.getItem("token");

            // Ensure correct data types
            const formattedNegotiationId = Number(negotiationId);
            const formattedQuantity = Number(invoiceDetails.quantity);
            const formattedAmount = Number(invoiceDetails.amount);
            const formattedUnitId = Number(invoiceDetails.unitId);

            if (isNaN(formattedNegotiationId) || isNaN(formattedQuantity) || isNaN(formattedAmount)) {
                toast.error("البيانات المدخلة غير صحيحة. يرجى التحقق من جميع الحقول المطلوبة.");
                return;
            }

            // Step 1: Check if there's an existing pending invoice
            const checkResponse = await axios.get(`${api_url}/invoices/${formattedNegotiationId}/pending`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (checkResponse.data.hasPendingInvoice) {
                toast.error("توجد فاتورة قيد الانتظار لهذا التفاوض.");
                return;
            }

            // Step 2: Submit the invoice if no pending one exists
            const response = await axios.post(
                `${api_url}/invoices`,
                {
                    negotiationId: formattedNegotiationId,
                    quantity: invoiceDetails.quantity,
                    amount: formattedAmount,
                    dueDate: invoiceDetails.dueDate,
                    notes: invoiceDetails.notes,
                    deliveryPlace: invoiceDetails.deliveryPlace, // New field
                    unitId: formattedUnitId, // New field
                    deliveryResponsibility: invoiceDetails.deliveryResponsibility, // New field
                    paymentTerms: invoiceDetails.paymentTerms, // New field
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            toast.success("تم إصدار الفاتورة بنجاح!");
            setShowInvoiceModal(false);
        } catch (error) {
            console.error("Failed to submit invoice:", error.response?.data || error.message);
            toast.error("فشل في إصدار الفاتورة");
        }
    };



    return (
        <div className="w-full h-full relative mt-10">

            <div className="mx-auto text-center lg:w-[66%] lg:border-2 border-primary rounded-2xl py-4 px-6 lg:px-10 bg-white">
                <div className="lg:pb-6 pb-4 flex-wrap flex gap-4 items-center justify-center lg:justify-between border-b-2 border-primary">

                    {currentUserId === negotiation?.sellerId && (
                        <button
                            onClick={handleIssueInvoice}
                            className="bg-primary text-white p-2 px-3 font-bold text-sm rounded-lg hover:scale-105 transition cursor-pointer"
                        >
                            إصدار فاتورة
                        </button>
                    )}

                    {negotiation?.itemName ? (
                        <p className="text-2xl font-bold md:block hidden">
                            طلب&nbsp;
                            {negotiation.negotiationType === "Service" ? "" : (`${negotiation.quantity} `)}

                            {negotiation.itemUnit}&nbsp;
                            - &nbsp;
                            {negotiation.itemName}
                        </p>
                    ) : (
                        <p>تحميل...</p>
                    )}

                    <div className="flex justify-center lg:justify-end items-center gap-1">
                        <span className="relative font-bold text-sm rounded-lg px-4 py-2 shadow-lg shadow-green-500/30 border border-primary flex items-center gap-2">
                            <span
                                className={`w-3 h-3 rounded-full ${true ? 'bg-green-500 shadow-lg shadow-green-500/30' : 'bg-gray-400'
                                    }`}
                            ></span>
                            {currentUserId === negotiation?.buyerId ? (
                                negotiation?.seller?.name || 'Seller'
                            ) : (
                                negotiation?.buyer?.name || 'Buyer'
                            )}
                        </span>
                    </div>

                </div>
                <NegotiationChatContainer messages={messages} currentUserId={currentUserId} />
                <div className="relative w-full">
                    <div className="h-16 border border-primary rounded-2xl flex items-center px-3 lg:px-6 bg-white">
                        {/* Send Button */}
                        <button
                            onClick={sendMessage}
                            disabled={sending || !newMessage}
                            className={`
                flex items-center justify-center px-4 py-2 text-white font-bold text-lg
                rounded-xl transition-all duration-200
                ${sending || !newMessage ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'}
            `}
                        >
                            إرسال
                        </button>

                        {/* Message Input */}
                        <input
                            type="text"
                            dir="rtl"
                            name="newMessage"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="اكتب رسالتك..."
                            className="border-0 bg-transparent w-full focus:ring-0 text-gray-800 text-base font-medium px-3 placeholder-gray-500 placeholder:font-normal"
                        />
                    </div>
                </div>
            </div>

            <InvoiceModal
                showInvoiceModal={showInvoiceModal}
                setShowInvoiceModal={setShowInvoiceModal}
                invoiceDetails={invoiceDetails}
                handleInvoiceChange={handleInvoiceChange}
                submitInvoice={submitInvoice}
                units={units} // Pass the units data if needed
            />
        </div>
    );
}

export default MahsoolNegotiation;
